import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Presentation, UserPlus, ArrowRight, Pencil, Globe, ExternalLink, CalendarDays, Ticket } from "lucide-react";
import SeminarFormDialog from "@/components/seminars/SeminarFormDialog";
import EnrollmentDialog from "@/components/seminars/EnrollmentDialog";
import { posterUrl, seminarWhen, priceLabel } from "@/lib/seminars";

function SeminarCard({ s, i, onEdit, onEnroll }) {
  return (
    <Card className="overflow-hidden border border-stone-200 rounded-xl shadow-none hover:border-[#1C352D] transition-colors animate-in-row flex" style={{ animationDelay: `${i * 20}ms` }} data-testid={`seminar-card-${s.id}`}>
      <div className="w-28 shrink-0 aspect-[4/5] bg-stone-100 grid place-items-center">
        {s.poster ? <img src={posterUrl(s)} alt="" className="w-full h-full object-cover"/> : <Presentation size={22} className="text-stone-300"/>}
      </div>
      <div className="p-4 flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">SEMINARIO</Badge>
          {s.cfu != null && <Badge className="bg-stone-100 text-stone-700 text-[10px] rounded-full">{s.cfu} CFU</Badge>}
          {s.public_booking && <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] rounded-full"><Globe size={10} className="mr-1"/>Online</Badge>}
        </div>
        <Link to={`/scuola/corsi/${s.id}`} className="block mt-1.5 font-medium text-[#1C352D] hover:underline truncate">{s.name}</Link>
        <div className="text-[11px] text-stone-500 mt-1 flex items-center gap-1"><CalendarDays size={11}/>{seminarWhen(s)}</div>
        <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-1"><Ticket size={11}/>Esterni: {priceLabel(s.external_price)}{s.external_seats ? ` · ${s.external_seats} posti` : ""}</div>
        <div className="mt-auto pt-3 flex gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => onEnroll(s)} data-testid={`seminar-enroll-${s.id}`}><UserPlus size={12} className="mr-1"/> Studenti</Button>
          <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => onEdit(s)} data-testid={`seminar-edit-${s.id}`}><Pencil size={12} className="mr-1"/> Modifica</Button>
          <Link to={`/scuola/corsi/${s.id}`}><Button size="sm" variant="ghost" className="rounded-full text-xs h-7">Apri <ArrowRight size={12} className="ml-1"/></Button></Link>
        </div>
      </div>
    </Card>
  );
}

export default function Seminars() {
  const [form, setForm] = useState({ open: false, seminar: null });
  const [enrollOpen, setEnrollOpen] = useState(null);
  const { data: seminars = [], isLoading } = useQuery({
    queryKey: ["seminars"],
    queryFn: async () => (await api.get("/courses", { params: { is_seminar: true } })).data,
  });
  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title="Seminari"
        subtitle="Seminari trasversali con iscrizione studenti e prenotazioni online per gli esterni."
        actions={
          <div className="flex gap-2 flex-wrap">
            <a href="/seminari" target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full" data-testid="seminar-public-link"><ExternalLink size={14} className="mr-1.5"/> Catalogo pubblico</Button></a>
            <Link to="/scuola/iscrizioni-seminari"><Button variant="outline" className="rounded-full" data-testid="seminar-bookings-link"><Ticket size={14} className="mr-1.5"/> Iscrizioni seminari</Button></Link>
            <Button data-testid="seminar-add-btn" onClick={() => setForm({ open: true, seminar: null })} className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"><Plus size={16} className="mr-1.5"/> Nuovo seminario</Button>
          </div>
        }
      />
      {isLoading ? <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
        : seminars.length === 0 ? (
          <Card className="p-14 text-center border border-dashed border-stone-300 rounded-xl">
            <Presentation size={32} className="mx-auto text-stone-400 mb-3"/>
            <div className="text-stone-500 text-sm">Nessun seminario creato ancora.</div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {seminars.map((s, i) => <SeminarCard key={s.id} s={s} i={i} onEdit={(x) => setForm({ open: true, seminar: x })} onEnroll={setEnrollOpen}/>)}
          </div>
        )}
      <SeminarFormDialog open={form.open} seminar={form.seminar} onOpenChange={(o) => setForm({ open: o, seminar: o ? form.seminar : null })}/>
      {enrollOpen && <EnrollmentDialog course={enrollOpen} onClose={() => setEnrollOpen(null)}/>}
    </div>
  );
}
